import * as zod from 'zod';
import SectionHeader from "../CoreComponents/Details/SectionHeader"
import { fetchApi, splitOnCapitals } from '../../Helpers/Functions';
import FormInput from '../CoreComponents/ReactFormComponents/FormInput';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { setModalOpen } from '../../store/Utilities';
import { useDispatch, useSelector } from 'react-redux';
import { SapVersionOptions, URLs } from '../../Constants/Consts';
import { TAddProjectAPIResponse, TProjectFormData } from '../../types/types';
import { useQuery } from '@tanstack/react-query';
import { TRefetchFn } from '../../Helpers/CustomHooks/useAllProjects';
import FormDDL from '../CoreComponents/ReactFormComponents/FormDDL';
import { RootState } from '../../store/store';
import { toast } from 'react-toastify';
import ReactLoading from 'react-loading';


type TProps = {
    projectNumber?: number;
    handleRefetchProjects?: TRefetchFn;
}

const schema = zod.object({
    projectName: zod.string().min(1, { message: "Please enter the project name" }),
    projectNumber: zod.string().min(1, { message: "Please enter the project number." }),
    projectLocation: zod.string().min(1, { message: "Please enter the project location." }),
    sapVersion: zod.number({ invalid_type_error: "Please select a SAP version." }).min(1, { message: "Please select a SAP version." }),
    country: zod.string().min(1, { message: "Please enter the country." }),
    isArchived: zod.boolean().nullable().optional(),
    region: zod.number().min(1, { message: "Please select a region." }),
    businessUnit: zod.number().min(1, { message: "Please select a region." }).optional()
});


export function AddProjectForm({ projectNumber, handleRefetchProjects }: TProps) {
    const isEditMode = Boolean(projectNumber);
    const dispatch = useDispatch();

    const { Regions } = useSelector((state: RootState) => state.Regions);
    const { BusinessUnites } = useSelector((state: RootState) => state.BusinessUnites);
    const { data: editedProjectData, isLoading } = useQuery({
        queryKey: ['projectData', projectNumber],
        queryFn: async () => {
            if (isEditMode) {
                const response = await fetchApi(`${URLs.postNewProjectUrl()}/${projectNumber}`);
                const data = await response.json() as TProjectFormData;
                const region = Regions.find(region => region.regionId === data.regionId);
                const businessUnite = BusinessUnites.find(businessUnite => businessUnite.businessUnitId === data.businessUnitId);
                reset({
                    ...data,
                    projectNumber: data.projectNumber?.toString() ?? "",
                    region: region ? region.regionId : undefined,
                    businessUnit: businessUnite ? businessUnite.businessUnitId : undefined,
                    sapVersion: data.sapVersionId,
                });
                return data;
            }
            return null;
        },
        enabled: !!projectNumber,
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        reset,
        setValue,
    } = useForm<AssignmentFormType>({
        resolver: zodResolver(schema),
    });

    type AssignmentFormType = zod.infer<typeof schema>;
    const handleCloseModal = () => { dispatch(setModalOpen(false)); reset() }

    const onSubmit = async (data: AssignmentFormType) => {

        const projectData = {
            ...editedProjectData,
            projectName: data.projectName,
            projectNumber: +data.projectNumber,
            projectLocation: data.projectLocation,
            sapVersionId: data.sapVersion,
            country: data.country,
            regionId: data.region,
            businessUnitId: data.businessUnit,
            isArchived: false
        };

        const url = isEditMode ? `${URLs.postNewProjectUrl()}/${projectNumber}` : URLs.postNewProjectUrl();
        const method = isEditMode ? "PUT" : "POST";
        try {

            const response = await fetchApi(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(projectData)
            });
            const addProjectResponse: TAddProjectAPIResponse = await response.json()
            if (addProjectResponse.success) {
                dispatch(setModalOpen(false));
                handleRefetchProjects()
                toast.success(addProjectResponse?.message || "Project added successfully");
                reset();
            } else throw new Error(addProjectResponse.message || "Failed to add project");

        } catch (error) {
            console.error("Error adding project:", error);
            toast.error(error.message || "An error occurred while adding the project.");
        }
    };

    const regionsOptions = Regions.map(region => ({
        text: region.regionDescription,
        value: region.regionId
    }))
    const watchProjectRegion = useWatch({ control, name: "region" });

    const businessUnitsOptions = BusinessUnites.filter(item => item.regionId === watchProjectRegion).map(businessUnit => ({
        text: businessUnit.businessUnitDescription,
        value: businessUnit.businessUnitId,
    }))

    const isBusinessUniteAvailable = Boolean(watchProjectRegion) && businessUnitsOptions.length > 0;
    const showLoadingOverlay = isEditMode && isLoading;

    return <div className='max-w-screen-md mt-4 relative'>
        {showLoadingOverlay && (
            <div
                className='absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 dark:bg-black/70 backdrop-blur-sm rounded'
                data-testid="add-project-form-loading"
                aria-busy="true"
                aria-live="polite"
            >
                <ReactLoading type="spin" color="#fdb419" height={48} width={48} />
                <div className='mt-4 text-grey-darkest dark:text-gray-100 font-medium'>Loading project details...</div>
            </div>
        )}
        <div className={showLoadingOverlay ? 'invisible' : ''}>
            <div className="mt-8 mb-8">
                <SectionHeader title="Project Details" />
            </div>

            <form className='' onSubmit={handleSubmit(onSubmit)}>
                <FormInput
                    dataTestId="project-name-input"
                    required={true}
                    errors={errors}
                    defaultValue=""
                    inputKey="projectName"
                    registeredProps={register('projectName')}
                    label={splitOnCapitals("Project Name")}
                    placeholder="Project Name"
                />
                <FormInput
                    dataTestId="project-number-input"
                    required={true}
                    errors={errors}
                    defaultValue={undefined}
                    inputKey="projectNumber"
                    registeredProps={register('projectNumber')}
                    label={splitOnCapitals("Project Number")}
                    placeholder="Project Number"
                    disabled={isEditMode}
                />
                <FormInput
                    dataTestId="project-location-input"
                    required={true}
                    errors={errors}
                    defaultValue=""
                    inputKey="projectLocation"
                    registeredProps={register('projectLocation')}
                    label={splitOnCapitals("Project Location")}
                    placeholder="Project Location"
                />
                <FormDDL
                    dataTestId="sap-version-dropdown-input"
                    options={SapVersionOptions}
                    control={control}
                    errors={errors}
                    defaultValue=""
                    label="SAP Version"
                    inputKey="sapVersion"
                    name="sapVersion"
                    onChangeEvent={e => {
                        const selectedSapVersion = e.target.value ? Number(e.target.value) : undefined;
                        setValue("sapVersion", selectedSapVersion);
                    }}
                    required={true}
                />
                <FormInput
                    dataTestId="country-input"
                    required={true}
                    errors={errors}
                    defaultValue=""
                    inputKey="country"
                    registeredProps={register('country')}
                    label={splitOnCapitals("Country")}
                    placeholder="Country"
                />
                <FormDDL
                    dataTestId="region-dropdown-input"
                    options={regionsOptions}
                    control={control}
                    errors={errors}
                    defaultValue=""
                    label="Region"
                    inputKey="region"
                    name="region"
                    onChangeEvent={e => {
                        const selectedRegionId = e.target.value ? Number(e.target.value) : undefined
                        setValue("region", selectedRegionId);

                    }}
                    required={true}
                />
                {isBusinessUniteAvailable &&
                    <FormDDL
                        dataTestId="business-unit-dropdown-input"
                        options={businessUnitsOptions}
                        control={control}
                        errors={errors}
                        defaultValue=""
                        label="Business Unit"
                        inputKey="businessUnit"
                        name="businessUnit"
                        onChangeEvent={e => {
                            const selectedBusinessUnite = e.target.value ? Number(e.target.value) : undefined;
                            setValue("businessUnit", selectedBusinessUnite);

                        }}
                    />}
                <div className='flex justify-between  mt-16'>
                    <button type="button"
                        data-testid="add-project-form-cancel-button"
                        onClick={handleCloseModal}
                        className=' mt-3 text-gray-900 bg-gray-500 dark:text-gray-900 dark:bg-gray-500'>Cancel
                    </button>
                    <button type="submit"
                        data-testid="add-project-form-submit-button"
                        className=' text-white bg-orange-1000 dark:text-gray-800 dark:bg-orange-1000'>Submit
                    </button>
                </div>
            </form>
        </div>
    </div>
}